<template>
  <div
    class="w-full min-h-screen pb-8 overflow-y-auto bg-cover"
    :style="'background-image: url(' + BackgroundImage + ')'"
  >
    <div class="flex items-center justify-center flex-1 mt-6">
      <div
        class="w-full max-w-100 py-8 px-5 sm:px-6 md:px-8 mx-5 bg-white dark:bg-slate-900 rounded-lg base-shadow"
      >
        <img src="/images/logo.png" alt="Logo" class="w-32 mb-6 mx-auto dark:invert dark:brightness-0" >
        <!-- <h1 class="text-2xl font-bold text-center text-primary font-manrope">Beesphone</h1> -->

        <div class="flex items-center mt-6">
          <NuxtLink
            to="signin"
            class="w-full border-b border-b-neutral-300 dark:border-b-slate-700 pt-3 pb-1 font-medium text-center text-[#182233] dark:text-slate-400 font-manrope hover:opacity-80 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Sign In
          </NuxtLink>
          <NuxtLink
            to="signup"
            class="border-b-2 border-b-primary w-full pt-3 pb-1 font-medium text-center text-[#182233] dark:text-slate-100 font-manrope hover:opacity-80 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Sign Up
          </NuxtLink>
        </div>

        <form
          class="mt-6"
          action=""
          method="post"
          @submit.prevent="handleSubmit"
        >
          <InputFields
            v-model="emailAddress"
            label="Email address"
            placeholder="Enter your email"
            :left-icon="HiOutlineMail"
            required
            type="email"
          />
          <SecureInput
            v-model="password"
            label="Password"
            placeholder="Enter your password"
            :left-icon="LuLock"
            required
            class="mt-4"
          />
          <SecureInput
            v-model="confirmPassword"
            label="Confirm password"
            placeholder="Confirm your password"
            :left-icon="LuLock"
            required
            class="mt-4"
          />

          <Transition
            enter-active-class="transition-all duration-200 ease-out"
            enter-from-class="opacity-0 -translate-y-1"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition-all duration-150 ease-in"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 -translate-y-1"
          >
            <AlertMessage v-if="message" :type="notificationError" :message="message" class="mt-4">
              <AlertIcon class="w-5 h-auto mr-3 min-w-5 shrink-0" stroke-class="" />
            </AlertMessage>
          </Transition>

          <HumanChallengeWidget
            :key="humanChallengeKey"
            class="mt-4"
            @verified="handleHumanVerification"
            @expired="handleHumanVerificationExpired"
            @error="handleHumanVerificationError"
          />

          <Button
            variant="primary"
            title="Sign Up"
            :disabled="isAuthenticating"
            size="lg"
            type="submit"
            class="w-full mt-6"
            :icon="HiOutlineArrowRight"
            icon-after-title
          />
        </form>

        <div class="flex items-center justify-center mt-6">
          <div class="h-px w-full bg-[#D9D9D9] dark:bg-slate-700" />
          <span class="px-4 text-sm font-medium text-[#39404F] dark:text-slate-400 font-manrope"
            >OR</span
          >
          <div class="h-px w-full bg-[#D9D9D9] dark:bg-slate-700" />
        </div>

        <Button
          variant="secondary"
          :title="isAuthenticating ? 'Signing in...' : 'Continue with Google'"
          size="lg"
          :disabled="isAuthenticating"
          class="w-full mt-6"
          icon="icons8-google"
          @click="onSignInWithGoogleButtonClick"
        >
          <template #icon>
            <img
              src="/images/google.png"
              alt="Google SignIn"
              class="mr-1 size-5"
            >
          </template>
        </Button>
        <Button
          variant="secondary"
          title="Continue with Apple"
          size="lg"
          class="w-full mt-2 opacity-50 cursor-not-allowed"
          icon="icons8-apple"
          @click="onSignInWithAppleButtonClick"
        >
          <template #icon>
            <img
              src="/images/apple.png"
              alt="Apple SignIn"
              class="mr-1 size-5 dark:invert dark:brightness-0"
            >
          </template>
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import SecureInput from "~/components/common/SecureInput.vue";
import Button from "~/components/common/Button.vue";
import AlertMessage from "~/components/common/AlertMessage.vue";
import HumanChallengeWidget from "~/components/auth/HumanChallengeWidget.vue";
import AlertIcon from "~/components/icons/AlertIcon.vue";
import InputFields from "~/components/common/InputFields.vue";
import BackgroundImage from "~/assets/images/bg-auth.png";
import { signInWithGoogle, signUp, PasswordValidationError } from "~/services/authentication";
import { authenticationErrorMap } from "~/constants/authenticationErrorMap";
import { logEvent, type Analytics } from "firebase/analytics";

import { HiOutlineMail, HiOutlineArrowRight } from "vue3-icons/hi";
import { LuLock } from "vue3-icons/lu";

definePageMeta({
  layout: "auth",
  title: "Sign Up",
});

const isAuthenticating = ref(false);
const emailAddress = ref("");
const password = ref("");
const confirmPassword = ref("");
const isCustomError = ref(false);
const message = ref<string>("");
const humanChallengeToken = ref("");
const humanChallengeKey = ref(0);
const notificationError = ref<"info" | "success" | "warning" | "error">(
  "success"
);

const router = useRouter();
const route = useRoute();
const redirectTo = route.query.redirectTo || "/";

const handleSubmit = async () => {
  if (!humanChallengeToken.value) {
    notificationError.value = "error";
    message.value = "Complete the human verification before signing up.";
    return;
  }

  isAuthenticating.value = true;
  try {
    await signUp({
      emailAddress: emailAddress.value,
      password: password.value,
      humanChallengeToken: humanChallengeToken.value,
    });
    // Deliberately redirect to sign-in rather than auto-logging in, so that
    // any required email-verification or account-approval steps are enforced
    // before the user gains an authenticated session.
    router.push("/auth/signin");
  } catch (err) {
    const authError = err as { code?: string; customError?: boolean };
    isCustomError.value = !!authError.customError;
    notificationError.value = "error";
    message.value =
      authenticationErrorMap[
        authError.code as keyof typeof authenticationErrorMap
      ] ||
      authError.code ||
      "Unable to sign up.";

    // Only reset the human challenge widget when the backend actually rejected
    // the token (or a network error prevented the request). For local
    // validation failures (e.g., weak password) the token is still valid and
    // resetting it forces an unnecessary re-challenge.
    if (!(err instanceof PasswordValidationError)) {
      humanChallengeToken.value = "";
      humanChallengeKey.value += 1;
    }
  } finally {
    isAuthenticating.value = false;
    setTimeout(() => {
      message.value = "";
    }, 2000);
  }
};

const onSignInWithGoogleButtonClick = async () => {
  if (isAuthenticating.value) return;

  isAuthenticating.value = true;
  try {
    await signInWithGoogle(redirectTo as string);
  } catch (error) {
    const authError = error as { code?: string; message?: string };
    isCustomError.value = false;
    notificationError.value = "error";
    message.value =
      authenticationErrorMap[authError.code as keyof typeof authenticationErrorMap] ||
      authError.message ||
      authError.code ||
      "Unable to sign in with Google.";
  } finally {
    isAuthenticating.value = false;
    setTimeout(() => {
      message.value = "";
    }, 2000);
  }
};

const _onSignInWithFacebookButtonClick = () => {
  console.log("onSignInWithFacebookButtonClick");
};

const onSignInWithAppleButtonClick = () => {
  // Apple sign-in is not yet enabled. Track taps to measure pre-launch interest.
  const analytics = useNuxtApp().$analytics as Analytics | undefined;
  if (analytics) {
    logEvent(analytics, 'apple_signin_interest', { source: 'signup' });
  }
};

const handleHumanVerification = (token: string) => {
  humanChallengeToken.value = token;
};

const handleHumanVerificationExpired = () => {
  humanChallengeToken.value = "";
};

const handleHumanVerificationError = () => {
  humanChallengeToken.value = "";
};
</script>
